import { EstateLogType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Loader from "@/shared/ui/Loader";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import useLoadHistory from "@/domains/estate/hooks/useLoadHistory";
import EstateHistoryLog from "@/domains/estate/components/history/EstateHistoryLog";
import EstateHistoryLogsFilter from "@/domains/estate/components/history/EstateHistoryLogsFilter.tsx";

export default function EstateHistoryContainer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("estate");
  const { loading, filter, setFilter, setPage, logs, totalPages, page } = useLoadHistory();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [shouldLoadNext, setShouldLoadNext] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || page >= totalPages) return;

    const threshold = 100;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      setShouldLoadNext(true);
    }
  }, [loading, page, totalPages]);

  useEffect(() => {
    if (!loading && shouldLoadNext) {
      setPage((prev) => prev + 1);
      setShouldLoadNext(false);
    }
  }, [loading, shouldLoadNext, setPage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return () => {};
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const header = <div className="text-18 text-white font-bold">{t("estate.history.title")}</div>;

  return (
    <DeviceTabContentWrapper
      header={header}
      onClose={onClose}
      scrollRef={scrollRef}
      className="p-0"
    >
      <EstateHistoryLogsFilter onSelectFilter={setFilter} selectedFilter={filter} />
      {logs.map((log, index) => {
        let text = t(`estate.history.logs.${log.entityType}`, {
          baseCharacter: `#${log.baseCharacter?.characterId} ${log.baseCharacter?.name}`,
          targetCharacter: `#${log.targetCharacter?.characterId} ${log.targetCharacter?.name}`,
          quantity: log.quantity,
          amount: log.amount,
          payload: log.payload,
        });

        if (log.entityType === EstateLogType.Bought && log.targetCharacter) {
          text = t("estate.history.logs.BoughtBetween", {
            targetCharacter: `#${log.targetCharacter?.characterId} ${log.targetCharacter?.name}`,
            amount: log.amount,
          });
        }

        if (
          log.entityType === EstateLogType.ImprovementBought &&
          typeof log.payload === "object" &&
          "type" in log.payload
        ) {
          text = t("estate.history.logs.ImprovementBought", {
            type: t(`estate.improvements.types.${log.payload.type}.name`),
          });
        }

        if (
          [EstateLogType.SubscriptionAdded, EstateLogType.SubscriptionRemoved].includes(
            log.entityType,
          ) &&
          typeof log.payload === "object" &&
          "type" in log.payload
        ) {
          text = t(`estate.history.logs.${log.entityType}`, {
            subscriptionName: t(`estate.improvements.subscriptions.${log.payload.type}.name`),
          });
        }

        if (
          [EstateLogType.DesignFurnitureUpdated, EstateLogType.DesignWallsUpdated].includes(
            log.entityType,
          ) &&
          typeof log.payload === "object" &&
          "nextName" in log.payload
        ) {
          text = t(`estate.history.logs.${log.entityType}`, {
            name: t(`estate.design.designVariants.${log.payload.nextName}.title`),
          });
        }

        return (
          <EstateHistoryLog
            key={log.id}
            characterName={`#${log.baseCharacter.characterId} ${log.baseCharacter.name}`}
            text={text}
            date={log.createdAt}
            isOdd={index % 2 === 0}
          />
        );
      })}
      {loading && <Loader />}
    </DeviceTabContentWrapper>
  );
}
